import { Getter } from '@devexpress/dx-react-core';
import {
    Column,
    GroupingState,
    IntegratedGrouping,
    IntegratedSorting,
    RowDetailState,
    SelectionState,
    Sorting,
    SortingState,
    Table as TableBase
} from '@devexpress/dx-react-grid';
import {
    Grid,
    Table,
    TableHeaderRow,
    TableRowDetail,
    TableSelection,
    VirtualTable,
    TableGroupRow
} from '@devexpress/dx-react-grid-material-ui';
import classNames from 'classnames';
import { upperCase, xor } from 'lodash';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Nullable } from '../../Shared/Types';
import { iconLight } from '../../Styles/Colours';
import { Button, ButtonMode, ButtonSize } from '../Button';
import { FloatingLinearProgress } from '../FloatingLinearProgress';
import { ArrowDownIcon, ArrowUpIcon } from '../Icons';
import styles from './DataTable.module.scss';
import { LoadMoreFooter } from './LoadMoreFooter';
import { GroupingRow } from '../../MedicationSummary/MedicationSummaryDataTable';
import { PbsRpbsRestrictionType, ProductPackGroup } from '../../Contracts/MedicationModels';

interface DataTableProps<DataType> {
    data: DataType[];
    columns: Column[];
    cellComponent: React.ComponentType<TableBase.DataCellProps>;
    headerComponent?: Nullable<React.ComponentType<TableHeaderRow.CellProps>>;
    defaultSort?: Sorting[];
    sortingControl?: SortingState.ColumnExtension[];
    customSort?: IntegratedSorting.ColumnExtension[];
    columnExtensions?: VirtualTable.ColumnExtension[];
    scrollToRowOnLoad?: any;
    loading?: boolean;
    limit?: number;
    disableColumnSorting?: boolean;
    hideButtonOnDisabledSortColumns?: boolean;
    rowSelection?: {
        selectedIds: string[];
        onSelect(row: DataType): void;
    };
    rowExpansion?: {
        toggleColumnWidth?: number;
        expandedIds: string[];
        buttonSize?: ButtonSize;
        onExpand(row: DataType): void;
        content(data: { row: DataType }): React.ReactElement<{}>;
    };
    testId: string;
    getRowId(data: DataType | GroupingRow): string;
    stubComponent?: Nullable<React.ComponentType<Table.CellProps>>;
    isMedicationBrowserGrid?: boolean;
}

function RootComponent(props: Grid.RootProps) {
    return (
        <Grid.Root
            {...props}
            style={{
                width: '100%'
            }}
        />
    );
}

function GroupRow<DataType>(props: {
    rowProps: TableGroupRow.RowProps;
}): JSX.Element {
    let groupLabel = '';
    switch (props.rowProps.row.value) {
        case ProductPackGroup.Unrestricted:
        case ProductPackGroup.Restricted:
            groupLabel = 'PBS/RPBS ' + upperCase(props.rowProps.row.value);
            break;
        case ProductPackGroup.AuthorityRequiredStreamlined:
            groupLabel = 'PBS/RPBS ' + upperCase('StreamlinedAuthority');
            break;
        case ProductPackGroup.AuthorityRequired:
            groupLabel = 'PBS/RPBS ' + upperCase('Authority');
            break;
        case ProductPackGroup.Private:
            groupLabel = upperCase(props.rowProps.row.value);
            break;
        case ProductPackGroup.S100:
            groupLabel = 'S100';
            break;
    }
    return (
        <tr className={styles.groupRow}>
            <td className={styles.groupCell} colSpan={11}>
                {groupLabel}
            </td>
        </tr>
    );
}

export function StyledTableCell(
    props: TableBase.DataCellProps & {
        className: string;
    }
) {
    return <Table.Cell {...props} className={classNames(styles.cell, props.className)} />;
}

interface ParentStyledDataCellProps extends TableBase.DataCellProps {
    parentStyle: string;
}
export const ParentStyledTableCell = (props: ParentStyledDataCellProps): JSX.Element => {
    const { parentStyle, ...otherProps } = props;
    return <Table.Cell {...otherProps} className={classNames(styles.cell, parentStyle)} />;
};

function TableComponent(props: any) {
    const { testId, ...otherProps } = props;
    return <Table.Table {...otherProps} data-test-id={`datatable${testId ? `-${testId}` : ''}-table`} />;
}

function TableBody(props: any) {
    return <Table.TableBody {...props} className={styles.body} />;
}

interface ParentStyledHeaderCellProps extends TableHeaderRow.CellProps {
    parentStyle?: string;
}
export const ParentStyledHeaderCell = (props: ParentStyledHeaderCellProps): JSX.Element => {
    const { parentStyle, ...otherProps } = props;
    const parentStyleClasses = parentStyle !== undefined ? parentStyle : null;
    return (
        <TableHeaderRow.Cell {...otherProps} className={classNames(styles.headerCell, parentStyleClasses)}>
            {props.children}
        </TableHeaderRow.Cell>
    );
};

function HeaderCell(props: TableHeaderRow.CellProps) {
    return (
        <TableHeaderRow.Cell {...props} className={styles.headerCell}>
            {props.children}
        </TableHeaderRow.Cell>
    );
}

function HeaderRow(props: Table.RowProps) {
    return <TableHeaderRow.Row {...props} className={styles.headerRow} />;
}

function TableHeaderStub(props: Table.CellProps) {
    return <Table.StubHeaderCell {...props} className={styles.headerStub} />;
}

export function TableRow(props: TableBase.DataRowProps) {
    return <Table.Row {...props} className={styles.row} />;
}

const RowComponent: React.FC<TableSelection.RowProps> = props => {
    const { selectByRowClick, highlighted, onToggle, ...otherProps } = props;
    const rowStyles = classNames(styles.selectionRow, { [styles.selected]: highlighted });
    const testId = props.tableRow.row?.id ?? props.tableRow.row?.code ?? undefined;
    const testName = props.tableRow.row?.name?.slice(0, 20) ?? undefined;

    return (
        <Table.Row
            {...otherProps}
            row={props.tableRow.row}
            onClick={props.onToggle}
            className={rowStyles}
            data-test-id={testId}
            data-test-name={testName}
        />
    );
};

export const HoverOnlyButtonGroup: React.FC = props => {
    return <div className={styles.hoverOnlyButtons}>{props.children}</div>;
};

// Currently hardcoded to white background, once we encounter multiple variants
// we can change the implementation to be a passed in component
function RowDetailCellComponent(props: TableRowDetail.CellProps) {
    return <TableRowDetail.Cell {...props} className={styles.rowDetailCellComponent} />;
}

function RowDetailRowComponent(props: TableRowDetail.RowProps) {
    return <TableRowDetail.Row {...props} className={styles.rowDetailRowComponent} />;
}

export function DataTable<DataType = any>(props: DataTableProps<DataType>) {
    const containerRef = useRef<Nullable<HTMLDivElement>>(null);
    const vtRef = useRef<Nullable<typeof VirtualTable>>(null);

    const [windowHeight, setWindowHeight] = useState(600);

    const calcHeight = useCallback(() => {
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            if (windowHeight !== rect.height) setWindowHeight(rect.height);
        }
    }, [windowHeight, containerRef.current]);

    const scrollToRow = useCallback(
        (rowId: any) => {
            if (vtRef.current) vtRef.current.scrollToRow(rowId);
        },
        [vtRef]
    );

    useEffect(() => {
        if (props.scrollToRowOnLoad && props.loading === false) {
            setTimeout(() => scrollToRow(props.scrollToRowOnLoad), 200);
        }
    }, [props.scrollToRowOnLoad, props.loading]);

    useEffect(() => {
        window.addEventListener('resize', calcHeight);

        return () => {
            window.removeEventListener('resize', calcHeight);
        };
    }, []);

    useEffect(() => {
        calcHeight();
    });

    const handleSelection = useCallback(
        (selectedIds: React.ReactText[]) => {
            if (props.rowSelection) {
                const changed = xor(props.rowSelection.selectedIds, selectedIds);
                if (changed.length > 0) {
                    const row = props.data.find((it: DataType) => props.getRowId(it) === changed[0]);
                    if (row) props.rowSelection.onSelect(row);
                }
            }
        },
        [props.data, props.rowSelection?.selectedIds]
    );

    const handleRowExpansion = useCallback(
        (expandedIds: React.ReactText[]) => {
            if (props.rowExpansion) {
                const changed = xor(props.rowExpansion.expandedIds, expandedIds);
                if (changed.length > 0) {
                    const row = props.data.find((it: DataType) => props.getRowId(it) === changed[0]);
                    if (row) props.rowExpansion.onExpand(row);
                }
            }
        },
        [props.data, props.rowExpansion?.expandedIds]
    );

    const ToggleCell = useCallback(
        (toggleCellProps: TableRowDetail.ToggleCellProps) => {
            const { expanded, onToggle, ...others } = toggleCellProps;

            const onClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
                event.stopPropagation();
                event.preventDefault();
                onToggle();
            };

            return (
                <TableRowDetail.Cell {...others} className={styles.detailCell}>
                    <Button
                        iconOnly
                        onClick={onClick}
                        size={props.rowExpansion?.buttonSize ?? ButtonSize.medium}
                        mode={ButtonMode.text}
                        noShadowOnHover
                        subtleShadeOnHover
                        testId='expand'
                    >
                        {expanded ? (
                            <ArrowUpIcon noPadding color={iconLight} />
                        ) : (
                            <ArrowDownIcon noPadding color={iconLight} />
                        )}
                    </Button>
                </TableRowDetail.Cell>
            );
        },
        [props.rowExpansion?.buttonSize]
    );

    const tableStyles: string = classNames(styles.tableContainer, {
        [styles.cellsInteractive]: props.rowSelection
    });

    const limitedData =
        !!Number(props.limit) && props.limit !== undefined
            ? props.data.filter((it, index) =>
                  !!Number(props.limit) && props.limit !== undefined ? index < props.limit : true
              )
            : props.data;

    const showLoadMoreFooter = !!props.limit && props.data.length > props.limit;

    const tableHeight = showLoadMoreFooter ? windowHeight - 50 : windowHeight;

    const SortLabelComponent = useMemo(
        () =>
            props.hideButtonOnDisabledSortColumns
                ? (sortLabelProps: TableHeaderRow.SortLabelProps) => {
                      if (sortLabelProps.disabled) {
                          return <>{sortLabelProps.children}</>;
                      } else {
                          return (
                              <TableHeaderRow.SortLabel {...sortLabelProps}>
                                  {sortLabelProps.children}
                              </TableHeaderRow.SortLabel>
                          );
                      }
                  }
                : TableHeaderRow.SortLabel,
        [props.hideButtonOnDisabledSortColumns]
    );

    return (
        <div className={tableStyles} ref={containerRef}>
            <FloatingLinearProgress display={props.loading} />
            <Grid rows={limitedData} getRowId={props.getRowId} rootComponent={RootComponent} columns={props.columns}>
                {!!props.rowExpansion && (
                    <RowDetailState
                        expandedRowIds={props.rowExpansion.expandedIds}
                        onExpandedRowIdsChange={handleRowExpansion}
                    />
                )}
                <SortingState
                    defaultSorting={props.defaultSort}
                    columnExtensions={props.sortingControl}
                    columnSortingEnabled={!props.disableColumnSorting}
                />
                <IntegratedSorting columnExtensions={props.customSort} />
                {props.isMedicationBrowserGrid && (
                    <GroupingState
                        grouping={[{ columnName: 'productPackGroup' }]}
                        expandedGroups={[
                            ProductPackGroup.Unrestricted,
                            ProductPackGroup.Restricted,
                            ProductPackGroup.AuthorityRequiredStreamlined,
                            ProductPackGroup.AuthorityRequired,
                            ProductPackGroup.Private,
                            ProductPackGroup.S100
                        ]}
                    />
                )}
                {props.isMedicationBrowserGrid && <IntegratedGrouping />}
                <VirtualTable
                    columnExtensions={props.columnExtensions}
                    height={tableHeight}
                    cellComponent={props.cellComponent}
                    tableComponent={tableProps => <TableComponent {...tableProps} testId={props.testId} />}
                    bodyComponent={TableBody}
                    rowComponent={TableRow}
                    stubHeaderCellComponent={props.stubComponent ? props.stubComponent : TableHeaderStub}
                    estimatedRowHeight={48}
                    ref={vtRef}
                />
                {!!props.rowExpansion && (
                    <TableRowDetail
                        toggleCellComponent={ToggleCell}
                        cellComponent={RowDetailCellComponent}
                        rowComponent={RowDetailRowComponent}
                        toggleColumnWidth={props.rowExpansion.toggleColumnWidth}
                        contentComponent={props.rowExpansion.content}
                    />
                )}
                {!!props.rowSelection && (
                    <SelectionState
                        selection={props.rowSelection?.selectedIds ?? []}
                        onSelectionChange={handleSelection}
                    />
                )}
                {!!props.rowExpansion && (
                    <Getter
                        name='tableColumns'
                        computed={({ tableColumns }) => {
                            const [detailColumn, ...restColumns] = tableColumns;
                            const detailColumnWider = {
                                ...detailColumn,
                                width: props.rowExpansion?.toggleColumnWidth ?? 64
                            };
                            return [...restColumns, detailColumnWider];
                        }}
                    />
                )}
                <TableHeaderRow
                    cellComponent={props.headerComponent ? props.headerComponent : HeaderCell}
                    showSortingControls={!props.disableColumnSorting}
                    rowComponent={HeaderRow}
                    sortLabelComponent={SortLabelComponent}
                />
                {!!props.rowSelection && (
                    <TableSelection
                        selectByRowClick
                        highlightRow
                        showSelectionColumn={false}
                        rowComponent={RowComponent}
                    />
                )}
                {props.isMedicationBrowserGrid && (
                    <TableGroupRow indentColumnWidth={1} rowComponent={rowProps => <GroupRow rowProps={rowProps} />} />
                )}
            </Grid>
            {showLoadMoreFooter && <LoadMoreFooter showing={props.limit} />}
        </div>
    );
}
